import React, { useEffect, useMemo, useState } from "react";
import {
  FaTimesCircle,
  FaClock,
  FaSpinner,
  FaCalendarAlt,
  FaTimes,
  FaExclamationTriangle,
} from "react-icons/fa";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";

interface OverdueInstallment {
  installmentId: number;
  studentName: string;
  studentEmail: string;
  batchName: string;
  courseName: string;
  dueDate: string;
  sequenceNumber: number;
  amount: number;
  daysOverdue: number;
}

const OverduePayments: React.FC = () => {
  const [installments, setInstallments] = useState<OverdueInstallment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [processingIds, setProcessingIds] = useState<Set<number>>(new Set());

  // Modal state
  const [showActionModal, setShowActionModal] = useState(false);
  const [selectedInstallment, setSelectedInstallment] =
    useState<OverdueInstallment | null>(null);
  const [newDueDate, setNewDueDate] = useState<string>("");

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/admin/payments/pending`,
        { credentials: "include" },
      );

      if (!response.ok) {
        throw new Error("Failed to fetch installments");
      }

      const allInstallments = await response.json();

      // Filter to show only UNPAID status
      const unpaidOnly = allInstallments.filter(
        (inst: any) => inst.status === "UNPAID",
      );

      // Calculate days overdue
      const today = new Date();
      const enriched = unpaidOnly.map((inst: any) => ({
        ...inst,
        daysOverdue: Math.floor(
          (today.getTime() - new Date(inst.dueDate).getTime()) /
            (1000 * 60 * 60 * 24),
        ),
      }));

      setInstallments(enriched);
    } catch (err: any) {
      setError(err.message || "Failed to load overdue payments");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const sortedInstallments = useMemo(() => {
    return [...installments].sort((a, b) => a.daysOverdue - b.daysOverdue);
  }, [installments]);

  // Pagination
  const totalPages = Math.ceil(sortedInstallments.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedInstallments = sortedInstallments.slice(startIndex, endIndex);

  const handleAllowRetryPayment = async (
    installmentId: number,
    dateToUse?: string,
  ) => {
    if (processingIds.has(installmentId)) return;

    setProcessingIds((prev) => new Set([...Array.from(prev), installmentId]));
    try {
      let url = `${process.env.REACT_APP_API_BASE_URL}/api/admin/payments/installments/${installmentId}/allow-retry`;
      if (dateToUse && dateToUse.trim() !== "") {
        url += `?newDueDate=${dateToUse}`;
      }
      const response = await fetch(url, {
        method: "PUT",
        credentials: "include",
      });
      if (!response.ok) throw new Error(await response.text());
      await fetchData();
      setError(null);
    } catch (err: any) {
      setError(err.message || "Failed to allow retry");
    } finally {
      setProcessingIds((prev) => {
        const s = new Set(prev);
        s.delete(installmentId);
        return s;
      });
    }
  };

  const handleActionClick = (installment: OverdueInstallment) => {
    setSelectedInstallment(installment);
    setShowActionModal(true);

    // Set default to next week
    const nextWeek = new Date();
    nextWeek.setDate(nextWeek.getDate() + 7);
    setNewDueDate(nextWeek.toISOString().split("T")[0]);
  };

  const handleModalAction = async () => {
    if (!selectedInstallment) return;
    await handleAllowRetryPayment(
      selectedInstallment.installmentId,
      newDueDate && newDueDate.trim() !== "" ? newDueDate : undefined,
    );
    closeModal();
  };

  const closeModal = () => {
    setShowActionModal(false);
    setSelectedInstallment(null);
    setNewDueDate("");
  };

  const setQuickPick = (days: number) => {
    const d = new Date();
    d.setDate(d.getDate() + days);
    setNewDueDate(d.toISOString().split("T")[0]);
  };

  const minToday = new Date().toISOString().split("T")[0];

  const PaginationControls = () => (
    <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-4 px-4 py-3 bg-white rounded-lg shadow-sm border">
      {/* Items per page */}
      <div className="flex items-center gap-2 text-sm">
        <span className="text-gray-600">Show:</span>
        <select
          value={itemsPerPage}
          onChange={(e) => {
            setItemsPerPage(Number(e.target.value));
            setCurrentPage(1);
          }}
          className="px-2 py-1 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#009fe3]"
        >
          <option value={10}>10</option>
          <option value={20}>20</option>
          <option value={50}>50</option>
          <option value={100}>100</option>
        </select>
        <span className="text-gray-600">per page</span>
      </div>

      {/* Page info */}
      <div className="text-sm text-gray-600">
        Showing {startIndex + 1} to{" "}
        {Math.min(endIndex, sortedInstallments.length)} of{" "}
        {sortedInstallments.length} installments
      </div>

      {/* Page buttons */}
      <div className="flex items-center gap-2">
        <button
          onClick={() => setCurrentPage(1)}
          disabled={currentPage === 1}
          className="px-3 py-1 text-sm rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          First
        </button>
        <button
          onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
          disabled={currentPage === 1}
          className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <FiChevronLeft />
        </button>

        {/* Page numbers */}
        <div className="flex items-center gap-1">
          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
            let pageNum: number;
            if (totalPages <= 5) {
              pageNum = i + 1;
            } else if (currentPage <= 3) {
              pageNum = i + 1;
            } else if (currentPage >= totalPages - 2) {
              pageNum = totalPages - 4 + i;
            } else {
              pageNum = currentPage - 2 + i;
            }

            return (
              <button
                key={pageNum}
                onClick={() => setCurrentPage(pageNum)}
                className={`px-3 py-1 text-sm rounded-lg border transition-colors ${
                  currentPage === pageNum
                    ? "bg-[#009fe3] text-white border-[#009fe3]"
                    : "border-gray-200 hover:bg-gray-50"
                }`}
              >
                {pageNum}
              </button>
            );
          })}
        </div>

        <button
          onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
          disabled={currentPage === totalPages}
          className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <FiChevronRight />
        </button>
        <button
          onClick={() => setCurrentPage(totalPages)}
          disabled={currentPage === totalPages}
          className="px-3 py-1 text-sm rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          Last
        </button>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <FaSpinner className="animate-spin text-5xl text-[#009fe3]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <FaTimesCircle className="text-red-600 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <h4 className="font-semibold text-red-800">Error</h4>
            <p className="text-sm text-red-600 mt-1">{error}</p>
          </div>
          <button
            onClick={() => setError(null)}
            className="text-red-600 hover:text-red-800"
          >
            <FaTimes />
          </button>
        </div>
      )}

      {/* Warning Banner */}
      <div className="bg-red-50 border-l-4 border-red-500 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <FaExclamationTriangle className="text-red-600 mt-0.5 flex-shrink-0" />
          <div>
            <h4 className="font-semibold text-red-800 mb-1">
              Overdue Payments Require Attention
            </h4>
            <p className="text-sm text-red-700">
              These students have missed their payment deadlines. Students with
              overdue payments may have their accounts deactivated. Use "Allow
              Retry" to extend their deadline.
            </p>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">
                  Student
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">
                  Course / Batch
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">
                  Installment
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">
                  Due Date
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">
                  Days Overdue
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">
                  Amount
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {paginatedInstallments.map((installment) => (
                <tr
                  key={installment.installmentId}
                  className="hover:bg-gray-50 transition-colors"
                >
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-900">
                      {installment.studentName}
                    </div>
                    <div className="text-xs text-gray-500">
                      {installment.studentEmail}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-sm text-gray-900">
                      {installment.courseName}
                    </div>
                    <div className="text-xs text-gray-500">
                      {installment.batchName}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900">
                    #{installment.sequenceNumber}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900">
                    {new Date(installment.dueDate).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold ${
                        installment.daysOverdue > 30
                          ? "bg-red-100 text-red-800"
                          : installment.daysOverdue > 14
                            ? "bg-orange-100 text-orange-800"
                            : "bg-yellow-100 text-yellow-800"
                      }`}
                    >
                      <FaClock />
                      {installment.daysOverdue} days
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm font-semibold text-gray-900">
                    ৳{installment.amount.toLocaleString()}
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => handleActionClick(installment)}
                      disabled={processingIds.has(installment.installmentId)}
                      className="px-3 py-1.5 bg-[#009fe3] text-white rounded-lg hover:bg-[#007ab8] transition-colors text-sm disabled:opacity-50"
                    >
                      {processingIds.has(installment.installmentId) ? (
                        <FaSpinner className="animate-spin" />
                      ) : (
                        "Allow Retry"
                      )}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {paginatedInstallments.length === 0 && (
          <div className="text-center py-12">
            <FaClock className="text-gray-400 text-5xl mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-700 mb-2">
              No Overdue Payments
            </h3>
            <p className="text-gray-600">
              All students have either paid or have upcoming deadlines.
            </p>
          </div>
        )}
      </div>

      {/* Pagination */}
      {sortedInstallments.length > 0 && <PaginationControls />}

      {/* Action Modal */}
      {showActionModal && selectedInstallment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full">
            {/* Modal Header */}
            <div className="sticky top-0 bg-gradient-to-r from-[#009fe3] to-[#0088cc] text-white p-6 rounded-t-xl">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-2xl font-bold">
                    Extend Payment Deadline
                  </h3>
                  <p className="text-blue-100 text-sm mt-1">
                    Installment #{selectedInstallment.sequenceNumber} -{" "}
                    {selectedInstallment.studentName}
                  </p>
                </div>
                <button
                  onClick={closeModal}
                  className="text-white hover:bg-white hover:bg-opacity-20 p-2 rounded-lg transition-colors"
                >
                  <FaTimes size={20} />
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="p-6 space-y-4">
              {/* Info */}
              <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
                <div>
                  <p className="text-xs text-gray-600">Course</p>
                  <p className="font-medium text-gray-900">
                    {selectedInstallment.courseName}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-600">Batch</p>
                  <p className="font-medium text-gray-900">
                    {selectedInstallment.batchName}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-600">Amount</p>
                  <p className="font-medium text-gray-900">
                    ৳{selectedInstallment.amount.toLocaleString()}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-600">Original Due Date</p>
                  <p className="font-medium text-gray-900">
                    {new Date(selectedInstallment.dueDate).toLocaleDateString()}
                  </p>
                </div>
                <div className="col-span-2">
                  <p className="text-xs text-gray-600">Days Overdue</p>
                  <p className="font-semibold text-red-600">
                    {selectedInstallment.daysOverdue} days
                  </p>
                </div>
              </div>

              {/* New Due Date Section */}
              <div className="border border-blue-100 rounded-xl p-4 bg-blue-50/50">
                <h4 className="text-sm font-semibold text-blue-800 mb-2">
                  Set New Payment Deadline
                </h4>
                <p className="text-xs text-blue-900/80 mb-3">
                  Choose a new due date to move this installment from{" "}
                  <span className="font-semibold">OVERDUE</span> to{" "}
                  <span className="font-semibold">UPCOMING</span>. This will
                  allow the student to submit payment again.
                </p>

                {/* Quick picks */}
                <div className="flex flex-wrap gap-2 mb-3">
                  {[7, 14, 30].map((d) => (
                    <button
                      key={d}
                      type="button"
                      onClick={() => setQuickPick(d)}
                      className={`px-3 py-1 rounded-full border text-xs ${
                        newDueDate ===
                        (() => {
                          const x = new Date();
                          x.setDate(x.getDate() + d);
                          return x.toISOString().split("T")[0];
                        })()
                          ? "bg-[#009fe3] text-white border-[#009fe3]"
                          : "bg-white text-blue-700 border-blue-300 hover:bg-blue-100"
                      }`}
                      title={`Set ${d} days from today`}
                    >
                      +{d} days
                    </button>
                  ))}
                </div>

                {/* Date input */}
                <div className="relative">
                  <FaCalendarAlt className="absolute left-3 top-1/2 -translate-y-1/2 text-blue-500" />
                  <input
                    type="date"
                    value={newDueDate}
                    onChange={(e) => setNewDueDate(e.target.value)}
                    min={minToday}
                    className="w-full pl-10 pr-3 py-2 border border-blue-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#009fe3] focus:border-transparent text-sm"
                    style={{ fontSize: "16px" }}
                  />
                </div>

                <p className="text-[11px] text-blue-900/70 mt-2">
                  Tip: Choose a date at least a few days ahead to give the
                  student enough time to pay.
                </p>
              </div>

              {/* Actions */}
              <div className="mt-2 grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={handleModalAction}
                  className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 bg-[#009fe3] text-white rounded-lg hover:bg-[#007ab8] transition-colors"
                >
                  <FaClock />
                  Extend Deadline
                </button>
                <button
                  type="button"
                  onClick={closeModal}
                  className="w-full inline-flex items-center justify-center px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OverduePayments;
